import React, { useMemo } from 'react';

import { t, Trans } from '@lingui/macro';
import { Button, SearchInput, Spinner, Table3 } from '@economic/taco';
import View, { useViewFilter } from '~/app/shared/components/View/View';
import CreateDimensionValueDialog from '~/app/pages/settings/categories-and-units/dimensions/dialogs/CreateDimensionValueDialog';
import { NO_DIMENSION_KEY } from '~/app/pages/reports/shared/components/Dimensions/constants';
import { getNoDimensionTranslation } from '~/app/pages/reports/shared/utils/dimensionsUtils';

import { DimensionLine, DimensionLineType } from '../types';

export interface DimensionLineListViewProps<T = object> {
    dimensionId: number;
    dimensionName?: string;
    dimensionLines?: DimensionLine[];
    enableCreate?: boolean;
    onCreated?: (data: T) => void;
    onSelected: (data: T) => void;
    includeDistributionKeys: boolean;
    includeNoDimensionValueOption?: boolean;
}

const DimensionLinesListView = ({
    dimensionId,
    dimensionName,
    dimensionLines: data,
    onSelected,
    onCreated,
    enableCreate,
    includeDistributionKeys,
    includeNoDimensionValueOption,
}: DimensionLineListViewProps<DimensionLine>) => {
    const [filter, setFilter, filterBy] = useViewFilter();
    const filteredData = useMemo(() => {
        let result: DimensionLine[] = [];

        if (typeof data !== 'undefined') {
            result = data.filter(value => value.active && (filterBy(value.name) || filterBy(value.key)));
        }

        if (includeNoDimensionValueOption && (result.length === 0 || result[0].key !== 0)) {
            result.unshift({
                dimensionId,
                key: NO_DIMENSION_KEY,
                name: getNoDimensionTranslation(dimensionName!),
                active: true,
                type: DimensionLineType.DimensionValue,
            });
        }

        return result;
    }, [data, filter]);

    if (typeof data === 'undefined') return <Spinner />;

    return (
        <View tabIndex={0}>
            <View.Title>{dimensionName ?? t`Dimension values`}</View.Title>
            <View.Header>
                {enableCreate ? (
                    <View.Header.Actions>
                        <Button
                            appearance="primary"
                            dialog={props => (
                                <CreateDimensionValueDialog.Unrouted
                                    {...props}
                                    showSaveAndApply
                                    dimensionId={dimensionId}
                                    onValueCreatedCallback={dimensionValue => {
                                        onCreated?.(dimensionValue);
                                    }}
                                />
                            )}>
                            <Trans>{`New ${dimensionName ?? 'dimension value'}`}</Trans>
                        </Button>
                    </View.Header.Actions>
                ) : null}
                <View.Header.Filters>
                    <SearchInput
                        autoFocus
                        onChange={e => setFilter(e.target.value)}
                        placeholder={t`Search for no. or name`}
                        value={filter}
                    />
                </View.Header.Filters>
            </View.Header>

            <div className="flex flex-col w-full">
                <Table3<DimensionLine>
                    id="dimension-value-list"
                    data={filteredData}
                    enableRowClick
                    onRowClick={row => onSelected(row)}>
                    <Table3.Column<DimensionLine> id="key" header={t`No.`} defaultWidth={75} sort="asc" />
                    <Table3.Column<DimensionLine> id="name" align="left" header={t`Name`} />
                    {includeDistributionKeys && (
                        <Table3.Column<DimensionLine>
                            id="type"
                            align="right"
                            header={t`Type`}
                            renderer={value =>
                                value === DimensionLineType.DimensionValue ? t`Dimension value` : t`Distribution key`
                            }
                        />
                    )}
                </Table3>
            </div>
        </View>
    );
};

export default DimensionLinesListView;
